import BasePageFilter from "@/domains/BasePageFilter/BasePageFilter";
import type Person    from "@/domains/Persons/Person";
import type Partner from "@/domains/Partners/Partner";
import Core from '@/core/Core';

class BuyerOrdersPageFilter extends BasePageFilter {
    static Events = {
        Updated: 'orders-page-filter:updated',
    } as const;
    protected storageKey = 'filters:buyer-orders';

    private _isMine: boolean;
    responsible: Person;
    seller: Partner;
    deliveryType: { id: number; name: string };
    orderCategory: { id: number; name: string };
    isOfferBased: { id: number; name: string; value: boolean | null };
    isSamples: { id: number; name: string; value: boolean | null };
    isForTestsOnly: { id: number; name: string; value: boolean | null };
    deliveryStatus: { id: string; name: string };
    paymentStatus: { id: string; name: string };

    // Константы дефолтных значений
    private static readonly DEFAULT_IS_MINE = false;
    private static readonly DEFAULT_IS_OFFER_BASED_ID = 3;
    static readonly DEFAULT_IS_SAMPLES_ID = 3;
    static readonly DEFAULT_IS_FOR_TESTS_ONLY_ID = 3;
    static readonly DEFAULT_DELIVERY_STATUS_ID = 'all';
    static readonly DEFAULT_PAYMENT_STATUS_ID = 'all';


    get isMine(): boolean {
        return this._isMine;
    }

    set isMine(value: boolean) {
        this._isMine = value;

        if (value === true) {
            this.responsible = Core.currentUser;
        } else {
            this.responsible = undefined;
        }
    }

    reset(): void {
        this._isMine = BuyerOrdersPageFilter.DEFAULT_IS_MINE;
        this.responsible = undefined;
        if (this.isLocked('seller') === false) {
            this.seller = undefined;
        }
        if (this.isLocked('deliveryType') === false) {
            this.deliveryType = undefined;
        }
        if (this.isLocked('orderCategory') === false) {
            this.orderCategory = undefined;
        }
        if (this.isLocked('isOfferBased') === false) {
            this.isOfferBased = Core.dictionaries.orderIsOfferBased.get(BuyerOrdersPageFilter.DEFAULT_IS_OFFER_BASED_ID);
        }
        if (this.isLocked('isSamples') === false) {
            this.isSamples = Core.dictionaries.orderIsSamples.get(BuyerOrdersPageFilter.DEFAULT_IS_SAMPLES_ID);
        }
        if (this.isLocked('isForTestsOnly') === false) {
            this.isForTestsOnly = Core.dictionaries.orderIsForTestsOnly.get(BuyerOrdersPageFilter.DEFAULT_IS_FOR_TESTS_ONLY_ID);
        }
        if (this.isLocked('deliveryStatus') === false) {
            this.deliveryStatus = Core.dictionaries.orderDeliveryStatus.get(BuyerOrdersPageFilter.DEFAULT_DELIVERY_STATUS_ID);
        }
        if (this.isLocked('paymentStatus') === false) {
            this.paymentStatus = Core.dictionaries.orderPaymentStatus.get(BuyerOrdersPageFilter.DEFAULT_PAYMENT_STATUS_ID);
        }
    }

    protected deserialize(json: string): void {
        const parsed = JSON.parse(json);

        this._isMine = parsed.isMine !== undefined ? parsed.isMine : BuyerOrdersPageFilter.DEFAULT_IS_MINE;
        this.responsible = parsed.responsible;
        this.seller = parsed.seller;
        this.deliveryType = parsed.deliveryType;
        this.orderCategory = parsed.orderCategory;

        // Мапим на словари - либо значение из стора, либо дефолт
        this.isOfferBased = parsed.isOfferBased !== undefined
            ? parsed.isOfferBased
            : Core.dictionaries.orderIsOfferBased.get(BuyerOrdersPageFilter.DEFAULT_IS_OFFER_BASED_ID);

        this.isSamples = parsed.isSamples !== undefined
            ? parsed.isSamples
            : Core.dictionaries.orderIsSamples.get(BuyerOrdersPageFilter.DEFAULT_IS_SAMPLES_ID);

        this.isForTestsOnly = parsed.isForTestsOnly !== undefined
            ? parsed.isForTestsOnly
            : Core.dictionaries.orderIsForTestsOnly.get(BuyerOrdersPageFilter.DEFAULT_IS_FOR_TESTS_ONLY_ID);

        this.deliveryStatus = parsed.deliveryStatus !== undefined
            ? parsed.deliveryStatus
            : Core.dictionaries.orderDeliveryStatus.get(BuyerOrdersPageFilter.DEFAULT_DELIVERY_STATUS_ID);

        this.paymentStatus = parsed.paymentStatus !== undefined
            ? parsed.paymentStatus
            : Core.dictionaries.orderPaymentStatus.get(BuyerOrdersPageFilter.DEFAULT_PAYMENT_STATUS_ID);
    }

    getRequestParams(): Record<string, any> {
        const params: any = {};

        /** Объекты отдаём с name: грид подставляет параметры в фильтры колонок по ключу,
         *  а селект колонки показывает значение по name */
        if (this.responsible?.id) params.responsible = { id: this.responsible.id, name: this.responsible.name };
        if (this.seller?.id) params.seller = { id: this.seller.id, name: this.seller.name };
        if (this.deliveryType?.id) params.deliveryType = { id: this.deliveryType.id, name: this.deliveryType.name };
        if (this.orderCategory?.id) params.orderCategory = { id: this.orderCategory.id, name: this.orderCategory.name };
        if (typeof this.isOfferBased?.value === 'boolean') params.isOfferBased = this.isOfferBased.value;
        if (typeof this.isSamples?.value === 'boolean') params.isSamples = this.isSamples.value;
        if (typeof this.isForTestsOnly?.value === 'boolean') params.isForTestsOnly = this.isForTestsOnly.value;
        if (this.deliveryStatus?.id !== 'all') params.deliveryStatus = this.deliveryStatus.id;
        if (this.paymentStatus?.id !== 'all') params.paymentStatus = this.paymentStatus.id;

        return this.excludeInactiveFields(params);
    }
}

export default BuyerOrdersPageFilter;
